/**
 * User-Agent que el front le reporta al backend en `X-Client-UA`.
 *
 * Por qué: los navegadores internos de Meta (Facebook, Messenger, Instagram en Android)
 * ponen su marca en `navigator.userAgent` pero no en la cabecera `User-Agent` de las
 * llamadas a la API; y Chrome en Android reduce la suya a «Android 10; K», sin el
 * modelo del teléfono. Core guarda este valor con cada sesión y el panel de soporte
 * «Dispositivos y navegadores» lo clasifica. Ver
 * `apps/core/src/app/modules/user/utils/client-user-agent.util.ts`.
 */

export const CLIENT_UA_HEADER = 'X-Client-UA';
const MAX_LENGTH = 512;

/** Lo mínimo de `navigator` que se usa, para poder probarlo sin un navegador real. */
export interface NavigatorLike {
  userAgent?: string;
  userAgentData?: {
    getHighEntropyValues?: (hints: string[]) => Promise<{ model?: string; platformVersion?: string }>;
  };
}

let cached: string | null = null;

/**
 * Una cabecera HTTP solo admite ASCII imprimible: un carácter raro haría fallar la
 * llamada entera. Se limpia y se corta al tope que acepta el backend.
 */
export function toHeaderSafe(ua: string): string {
  return ua.replace(/[^\x20-\x7E]/g, '').trim().slice(0, MAX_LENGTH);
}

/**
 * Chrome en Android manda «(Linux; Android 10; K)» para todos los teléfonos. Con los
 * User-Agent Client Hints se puede pedir el modelo y la versión reales y reponerlos,
 * que es justo para lo que existen esas pistas.
 */
export function withAndroidModel(ua: string, hints: { model?: string; platformVersion?: string } | null | undefined): string {
  const model = (hints?.model ?? '').trim();
  if (!model || /[;()]/.test(model)) return ua;
  const major = String(hints?.platformVersion ?? '').split('.')[0];
  const version = /^\d+$/.test(major) ? major : '10';
  return ua.replace('(Linux; Android 10; K)', `(Linux; Android ${version}; ${model})`);
}

/**
 * Calcula el valor una vez al arrancar. Nunca falla: si el navegador no expone las
 * pistas (Safari, Firefox) o las niega, queda `navigator.userAgent` tal cual.
 */
export async function initClientUserAgent(nav: NavigatorLike | undefined = globalThis.navigator as NavigatorLike | undefined): Promise<string> {
  const base = nav?.userAgent ?? '';
  let hints: { model?: string; platformVersion?: string } | null = null;
  try {
    hints = (await nav?.userAgentData?.getHighEntropyValues?.(['model', 'platformVersion'])) ?? null;
  } catch {
    hints = null;
  }
  cached = toHeaderSafe(withAndroidModel(base, hints));
  return cached;
}

/** El valor calculado al arrancar o, si todavía no terminó, `navigator.userAgent`. */
export function getClientUserAgent(nav: NavigatorLike | undefined = globalThis.navigator as NavigatorLike | undefined): string {
  return cached ?? toHeaderSafe(nav?.userAgent ?? '');
}

/** Solo para pruebas. */
export function resetClientUserAgentForTests(): void {
  cached = null;
}
